<div class="natural-language-criteria-session">
    <!-- Header -->
    <div class="flex align-items-center gap-2 mb-3">
        <i class="fa-solid fa-comments text-primary text-xl"></i>
        <span class="text-lg font-semibold">Natürlichsprachliche Selektionskriterien</span>
        <span *ngIf="sessionResponse" class="text-sm ml-2" [ngClass]="getStatusColor(sessionResponse.status)">
            ({{ formatStatus(sessionResponse.status) }})
        </span>
    </div>

    <p class="text-sm text-500 mb-3">
        Beschreiben Sie Ihre Zielgruppe in einfacher Sprache. Sie können die Kriterien im Dialog verfeinern.
    </p>

    <!-- Initial Query Input (only shown when no session) -->
    <div *ngIf="!sessionResponse" class="flex flex-column gap-3">
        <div class="p-inputgroup">
            <span class="p-inputgroup-addon">
                <i class="fa-solid fa-wand-magic-sparkles"></i>
            </span>
            <textarea
                pInputTextarea
                [(ngModel)]="query"
                [rows]="3"
                class="w-full"
                placeholder="z.B. 'Kunden in Bayern mit Verträgen, die in 30 Tagen ablaufen'"
                [disabled]="processing">
            </textarea>
        </div>

        <div class="flex align-items-center justify-content-between">
            <p-button
                label="Beispiele anzeigen"
                icon="fa-solid fa-lightbulb"
                styleClass="p-button-text p-button-sm"
                (onClick)="showExamples = !showExamples">
            </p-button>
            <p-button
                label="Sitzung starten"
                icon="fa-solid fa-play"
                (onClick)="startSession()"
                [loading]="processing"
                [disabled]="!query.trim()">
            </p-button>
        </div>

        <!-- Examples Panel -->
        <div *ngIf="showExamples" class="p-3 bg-gray-100 border-round">
            <div class="text-sm font-medium mb-2">Beispielabfragen:</div>
            <div class="flex flex-column gap-2">
                <div *ngFor="let example of exampleQueries"
                     class="p-2 bg-white border-round cursor-pointer hover:surface-200"
                     (click)="useExample(example)">
                    <i class="fa-solid fa-arrow-right text-primary mr-2"></i>
                    <span class="text-sm">{{ example }}</span>
                </div>
            </div>
        </div>
    </div>

    <!-- Error Message -->
    <p-message *ngIf="errorMessage" severity="error" [text]="errorMessage" styleClass="mt-3 w-full"></p-message>

    <!-- Processing Indicator -->
    <div *ngIf="processing" class="flex align-items-center justify-content-center p-4">
        <i class="fa-solid fa-spinner fa-spin text-2xl text-primary mr-2"></i>
        <span>Verarbeitung...</span>
    </div>

    <!-- Active Session View -->
    <div *ngIf="sessionResponse && !processing">
        <!-- Session Info Bar -->
        <div class="flex align-items-center justify-content-between p-3 bg-gray-100 border-round mb-3">
            <div class="flex align-items-center gap-3">
                <div>
                    <div class="text-xs text-500">Konfidenz</div>
                    <div class="text-sm font-semibold" [ngClass]="getConfidenceColor(sessionResponse.confidenceScore)">
                        {{ getConfidenceLabel(sessionResponse.confidenceScore) }}
                        ({{ (sessionResponse.confidenceScore * 100) | number:'1.0-0' }}%)
                    </div>
                </div>
                <div class="border-left-1 border-300 pl-3">
                    <div class="text-xs text-500">Gesch. Mitglieder</div>
                    <div class="text-sm font-semibold">{{ sessionResponse.estimatedMemberCount | number }}</div>
                </div>
                <div class="border-left-1 border-300 pl-3">
                    <div class="text-xs text-500">Verfeinerungen</div>
                    <div class="text-sm font-semibold">{{ sessionResponse.refinementCount }}</div>
                </div>
            </div>
            <p-button
                label="Abbrechen"
                icon="fa-solid fa-times"
                styleClass="p-button-text p-button-danger p-button-sm"
                (onClick)="abandonSession()"
                *ngIf="isSessionActive">
            </p-button>
        </div>

        <!-- Conversation History -->
        <div class="conversation-history mb-4" style="max-height: 300px; overflow-y: auto;">
            <div *ngFor="let message of sessionResponse.conversationHistory"
                 class="flex gap-2 mb-3"
                 [ngClass]="{'justify-content-end': message.role === 'USER'}">
                <div *ngIf="message.role === 'SYSTEM'" class="flex-shrink-0">
                    <div class="w-2rem h-2rem border-circle bg-primary flex align-items-center justify-content-center">
                        <i class="fa-solid fa-robot text-white text-sm"></i>
                    </div>
                </div>
                <div class="p-3 border-round max-w-30rem"
                     [ngClass]="{
                         'bg-primary text-white': message.role === 'USER',
                         'bg-gray-100': message.role === 'SYSTEM'
                     }">
                    <div class="text-sm">{{ message.content }}</div>
                    <div class="text-xs mt-1" [ngClass]="{'text-gray-300': message.role === 'USER', 'text-500': message.role === 'SYSTEM'}">
                        {{ message.timestamp | date:'HH:mm' }}
                    </div>
                </div>
                <div *ngIf="message.role === 'USER'" class="flex-shrink-0">
                    <div class="w-2rem h-2rem border-circle bg-blue-600 flex align-items-center justify-content-center">
                        <i class="fa-solid fa-user text-white text-sm"></i>
                    </div>
                </div>
            </div>
        </div>

        <!-- Current Interpretation -->
        <div class="p-3 bg-blue-50 border-round mb-4">
            <div class="flex align-items-center gap-2 mb-2">
                <i class="fa-solid fa-info-circle text-blue-600"></i>
                <span class="font-medium text-blue-800">Aktuelle Interpretation</span>
            </div>
            <div class="text-sm text-blue-700">{{ sessionResponse.interpretation }}</div>
        </div>

        <!-- Clarification Questions -->
        <div *ngIf="hasPendingQuestions" class="p-card mb-4">
            <div class="p-card-body">
                <div class="flex align-items-center gap-2 mb-3">
                    <i class="fa-solid fa-question-circle text-yellow-600"></i>
                    <span class="font-medium">Bitte klären Sie Folgendes:</span>
                </div>

                <div *ngFor="let question of sessionResponse.pendingQuestions" class="mb-4 pb-3 border-bottom-1 border-200">
                    <div class="text-sm font-medium mb-2">{{ question.questionText }}</div>

                    <div *ngIf="question.ambiguousTerm" class="text-xs text-500 mb-2">
                        Bezogen auf: "{{ question.ambiguousTerm }}"
                    </div>

                    <!-- Suggested Options -->
                    <div class="flex flex-wrap gap-2 mb-2" *ngIf="question.suggestedOptions?.length">
                        <p-button
                            *ngFor="let option of question.suggestedOptions; let i = index"
                            [label]="option"
                            styleClass="p-button-sm"
                            [class]="selectedOptions[question.questionId] === i ? 'p-button-primary' : 'p-button-outlined'"
                            (onClick)="selectOption(question, i)">
                        </p-button>
                    </div>

                    <!-- Custom Response Input -->
                    <div class="p-inputgroup p-inputgroup-sm mt-2">
                        <input type="text"
                               pInputText
                               placeholder="Oder eigene Antwort eingeben..."
                               [value]="clarificationResponses[question.questionId] || ''"
                               #customInput
                               (input)="setCustomResponse(question, customInput.value)">
                    </div>
                </div>

                <div class="flex justify-content-end">
                    <p-button
                        label="Klärungen absenden"
                        icon="fa-solid fa-check"
                        (onClick)="provideClarification()">
                    </p-button>
                </div>
            </div>
        </div>

        <!-- Refinement Input (only for active sessions without pending questions) -->
        <div *ngIf="isSessionActive && !hasPendingQuestions" class="mb-4">
            <div class="text-sm font-medium mb-2">Kriterien verfeinern:</div>
            <div class="p-inputgroup">
                <span class="p-inputgroup-addon">
                    <i class="fa-solid fa-edit"></i>
                </span>
                <input type="text"
                       pInputText
                       [(ngModel)]="refinementInput"
                       placeholder="z.B. 'Auch Kunden aus Hamburg einschließen' oder 'Inaktive Benutzer ausschließen'"
                       (keyup.enter)="refineCriteria()">
                <p-button
                    icon="fa-solid fa-paper-plane"
                    (onClick)="refineCriteria()"
                    [disabled]="!refinementInput.trim()">
                </p-button>
            </div>
            <div class="text-xs text-500 mt-1">
                Beispiele: "Auch einschließen...", "Ausschließen...", "Ändern... zu...", "Eingrenzen auf..."
            </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-content-end gap-2 mt-4">
            <p-button
                label="Neu beginnen"
                styleClass="p-button-outlined p-button-secondary"
                icon="fa-solid fa-redo"
                (onClick)="reset()">
            </p-button>
            <p-button
                *ngIf="canFinalize"
                label="Kriterien abschließen"
                styleClass="p-button-success"
                icon="fa-solid fa-check-double"
                (onClick)="finalizeSession()">
            </p-button>
            <p-button
                *ngIf="canApply"
                label="Auf Kampagne anwenden"
                icon="fa-solid fa-play"
                (onClick)="applyCriteria()">
            </p-button>
        </div>

        <!-- Applied Status -->
        <div *ngIf="sessionResponse.status === 'APPLIED'" class="mt-4">
            <p-message severity="success" text="Kriterien wurden erfolgreich auf die Kampagne angewendet."></p-message>
        </div>
    </div>
</div>
